/**
 * Hooks TanStack Query pour la feature Mémoire utilisateur (#271).
 *
 * Endpoint : GET/PATCH /cabinet-members/memory
 * Types    : MemoryBlock, MemoryConfigResponse depuis @hector/shared-types
 *
 * Pattern miroir de useAssistantSettings.ts — même structure query + mutation
 * + invalidation double (spécifique cabinetId + générale).
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { ErrorService } from "@/lib/errors";
import { useCabinetId } from "@/hooks/useCabinetMemberMe";
import type {
  MemoryConfigResponse,
  MemoryLabel,
} from "@hector/shared-types";

// ─── Query key helpers ────────────────────────────────────────────────────────

/**
 * Clé générale (pour l'invalidation broad sans cabinetId).
 *
 * Exportée parce que la mémoire a un second auteur — les outils `update_memory`
 * de l'agent — dont le rafraîchissement est câblé dans `sessionDoneBridge.ts`.
 * Un littéral recopié là-bas cesserait silencieusement de matcher le jour où
 * cette clé change.
 */
export const MEMORY_SETTINGS_KEY = "memory-settings";

/** Clé scoped cabinet — utilisée pour la query + invalidation spécifique */
export const memorySettingsKey = (cabinetId: string) =>
  [MEMORY_SETTINGS_KEY, cabinetId] as const;

// ─── GET hook ─────────────────────────────────────────────────────────────────

/**
 * Récupère les blocs de mémoire (user_profile, user_memory) du membre connecté.
 * Désactivé tant que cabinetId est absent (boot race — pattern systématique).
 */
export const useMemorySettings = () => {
  const cabinetId = useCabinetId();

  return useQuery({
    queryKey: cabinetId ? memorySettingsKey(cabinetId) : [MEMORY_SETTINGS_KEY],
    queryFn: () =>
      apiClient.get<MemoryConfigResponse>("/cabinet-members/memory"),
    enabled: !!cabinetId,
    staleTime: 5 * 60 * 1000, // 5 min — cohérent avec useAssistantSettings
    gcTime: 10 * 60 * 1000,
  });
};

// ─── PATCH mutation ───────────────────────────────────────────────────────────

export interface UpdateMemoryBlockPayload {
  label: MemoryLabel;
  value: string;
  showToast?: boolean;
  /** Version captured with the editing snapshot, not the latest query result. */
  expectedVersion?: number;
}

/**
 * Met à jour un bloc de mémoire (PATCH /cabinet-members/memory).
 * Invalide la clé scoped cabinetId ET la clé générale (règle monorepo :
 * invalider spécifique + général pour éviter les stale picks cross-context).
 */
export const useUpdateMemoryBlock = () => {
  const queryClient = useQueryClient();
  const cabinetId = useCabinetId();

  return useMutation({
    mutationFn: ({ label, value, expectedVersion }: UpdateMemoryBlockPayload) =>
      apiClient.patch<MemoryConfigResponse>("/cabinet-members/memory", {
        label,
        value,
        ...(expectedVersion !== undefined ? { expectedVersion } : {}),
      }),

    onSuccess: (_data, variables) => {
      // Invalidation spécifique (si cabinetId disponible)
      if (cabinetId) {
        queryClient.invalidateQueries({
          queryKey: memorySettingsKey(cabinetId),
        });
      }
      // Invalidation générale (couvre les contextes sans cabinetId et les
      // caches cross-cabinet qui existeraient dans des sessions staff)
      queryClient.invalidateQueries({
        queryKey: [MEMORY_SETTINGS_KEY],
      });

      if (variables.showToast !== false) {
        toast.success("Mémoire mise à jour.");
      }
    },

    onError: (error) => {
      if (error && typeof error === "object" && "statusCode" in error && error.statusCode === 409) {
        // Keep the draft on screen, but fetch the winning snapshot so the existing
        // external-update banner can offer it without silently authorizing overwrite.
        if (cabinetId) queryClient.invalidateQueries({ queryKey: memorySettingsKey(cabinetId) });
        queryClient.invalidateQueries({ queryKey: [MEMORY_SETTINGS_KEY] });
        toast("La mémoire a changé pendant votre saisie. Votre texte est conservé ; chargez la nouvelle version avant de reprendre vos modifications.");
        return;
      }
      // Pas de `userMessage` forcé : sur un refus (400), le serveur renvoie le
      // dépassement chiffré (« texte de 10112 caractères refusé — plafond 10000,
      // place restante… ») que `apiClient` place dans `error.message` et
      // qu'ErrorService affiche pour une validation. Un message générique ici
      // masquait ce diagnostic et laissait l'avocat réessayer à l'aveugle. Les
      // autres échecs (5xx, réseau) gardent le message centralisé d'ErrorService.
      ErrorService.handle(error, {
        context: { action: "update_memory_block" },
      });
    },
  });
};
